import { useMemo, useState } from "react";
import { formatCZK } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Search, X, FileText, ArrowRight, Briefcase, AlertTriangle, CheckCircle2, ChevronRight, RefreshCw, Lock } from "lucide-react";
import { InfoTip } from "@/components/superadmin/Subscription";
import { cardAction } from "@/components/superadmin/FirmActions";
import OcrStatsDialog from "@/components/superadmin/OcrStats";
import {
  TZ, norm, dayShort, daysTo, dny, plusDays, SEGMENTS, COLUMNS, segOf, colOf, sortFor, isStripe, periodAmount,
  currentInvoice, CYCLE_ADV, CYCLE_PERIOD, activity, pl, owedAmount, isPaying,
} from "@/components/superadmin/firm";

// Superadmin — varianta Pipeline (209). Tabule přes celou šířku: V demu → Platí → Fakturovat →
// Čeká na úhradu. Ostatní stavy (Pozastavené, Archiv, Testovací) přes segment. Každá firma je
// na obrazovce jen jednou; klik na kartu otevře celý detail firmy (CompanyPanel) zprava.

const SEG_NOTE = {
  pauza: "Teď nemůžou pracovat. Vrátíš je jedním klepnutím.",
  archiv: "Ukončená spolupráce a ukončená dema.",
  test: "Tvoje testovací účty — nepočítají se do čísel, nezamykají se.",
};
const COL_EMPTY = {
  demo: ["Nikdo v demu", ""],
  plati: ["Zatím nikdo", ""],
  fakt: ["Nic k fakturaci", "Faktura se vystaví 7 dní před koncem období."],
  ceka: ["Nikdo nedluží", "Všechny faktury jsou zaplacené."],
};
const DOT = { demo: "bg-muted-foreground", plati: "bg-success", fakt: "bg-amber-500", ceka: "bg-brand" };

function DaysRing({ n }) {
  const left = Math.max(0, n ?? 0), pct = Math.min(1, left / 14), r = 19, c = 2 * Math.PI * r;
  const urgent = left <= 3;
  return (
    <span className="relative w-12 h-12 shrink-0" aria-label={`Zbývá ${left} ${dny(left)}`} title={`Zbývá ${left} ${dny(left)}`}>
      <svg viewBox="0 0 48 48" className="w-12 h-12 -rotate-90" aria-hidden="true">
        <circle cx="24" cy="24" r={r} fill="none" strokeWidth="4" className="stroke-secondary" />
        <circle cx="24" cy="24" r={r} fill="none" strokeWidth="4" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct)}
          className={urgent ? "stroke-brand" : "stroke-foreground"} />
      </svg>
      <span className="absolute inset-0 grid place-items-center text-center leading-none">
        <span className={`text-[15px] font-black tabular-nums ${urgent ? "pp-ink" : ""}`}>{left}<span className="block text-[9px] font-semibold text-muted-foreground">{dny(left)}</span></span>
      </span>
    </span>
  );
}

function Pill({ tone = "muted", children }) {
  const cls = {
    danger: "border border-rose-500/60 text-rose-600 dark:text-rose-400",
    dangerSolid: "bg-rose-500/15 text-rose-600 dark:text-rose-400",
    warn: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    ok: "bg-success/15 text-success",
    violet: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
    muted: "bg-secondary text-muted-foreground",
  }[tone];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap ${cls}`}>{children}</span>;
}

function Strip({ tone, icon: Icon, children, onClick }) {
  const cls = { ok: "text-success", warn: "bg-amber-500/10 text-amber-800 dark:text-amber-300", brand: "bg-brand/10 pp-ink", muted: "text-muted-foreground" }[tone];
  const pad = tone === "ok" || tone === "muted" ? "" : "px-2.5 py-1.5 rounded-[10px]";
  const Tag = onClick ? "button" : "div";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick ? (e) => { e.stopPropagation(); onClick(); } : undefined}
      className={`flex items-start gap-1.5 w-full text-[13px] font-semibold leading-snug min-w-0 ${pad} ${cls} ${onClick ? "hover:underline text-left" : ""}`}>
      {Icon && <Icon size={14} className="shrink-0 mt-[2px]" aria-hidden="true" />}<span className="min-w-0 [overflow-wrap:anywhere]">{children}</span>
    </Tag>
  );
}

/** Obsah karty podle segmentu a sloupce — jedna funkce pro tabuli, mřížky i výsledky hledání. */
function cardModel(r, col, ctx) {
  const k = r.display_status?.key, b = r.billing || {}, ni = currentInvoice(b), seg = segOf(r);
  const m = { sub: null, subTone: "", right: null, rightSub: null, rightTone: "", pill: null, foot: [] };
  if (seg === "test") {
    m.pill = <Pill tone="violet">Testovací</Pill>;
    m.sub = r.plan_label;
    if (r.has_subscription) m.foot.push(<Strip key="s" tone="warn" icon={AlertTriangle}>Předplatné ve Stripe je pořád aktivní</Strip>);
    return m;
  }
  if (seg === "archiv") {
    m.pill = <Pill tone="muted">Ukončeno{r.status_at ? ` ${dayShort(r.status_at)}` : ""}</Pill>;
    m.sub = (r.plan || "demo") === "demo" ? "Demo · nepřešli do placení" : `${r.plan_label} · placený tarif`;
    return m;
  }
  if (seg === "pauza") {
    if (k === "pozastaveno") {
      m.pill = <Pill tone="danger">Pozastaveno ručně</Pill>;
      m.sub = `${r.plan_label}${r.status_at ? ` · od ${dayShort(r.status_at)}` : ""}`;
    } else if (k === "nezaplaceno" || k === "ceka_uhrada") {
      m.pill = <Pill tone="dangerSolid">Zamčeno · nezaplaceno</Pill>;
      m.sub = k === "nezaplaceno" ? `${r.plan_label} · zamčeno od ${dayShort(b.lock_on)}` : `${r.plan_label} · platba kartou neprošla`;
      m.right = formatCZK(owedAmount(r)); m.rightSub = "dluží"; m.rightTone = "text-rose-600 dark:text-rose-400";
    } else {
      m.pill = <Pill tone="danger">Demo vypršelo {dayShort(r.trial_ends_at)}</Pill>;
      m.sub = r.state === "blocked" ? `Zamčeno od ${dayShort(plusDays(r.trial_ends_at, 14))}` : `Jen čtení do ${dayShort(plusDays(r.trial_ends_at, 14))}, pak zámek`;
      m.foot.push(<Strip key="a" tone="muted" icon={Briefcase}>{activity(r)}</Strip>);
    }
    return m;
  }
  if (col === "demo") {
    const n = daysTo(r.trial_ends_at);
    m.sub = n != null && n <= 1 ? (n <= 0 ? "Demo končí dnes" : "Demo končí zítra") : `Demo do ${dayShort(r.trial_ends_at)}`;
    m.subTone = n != null && n <= 1 ? "pp-ink font-semibold" : "";
    m.ring = n;
    m.foot.push(r.active_projects > 0
      ? <Strip key="a" tone="muted" icon={Briefcase}>{activity(r)}</Strip>
      : <Strip key="a" tone="warn" icon={AlertTriangle}>Nic nezaložili · zavolej a pomoz</Strip>);
    return m;
  }
  const over = (r.limit_active_projects != null && r.active_projects > r.limit_active_projects) || (r.limit_users != null && r.user_count > r.limit_users);
  if (col === "plati") {
    m.sub = isStripe(r) ? `${r.plan_label} · kartou` : `${r.plan_label} · ${CYCLE_ADV[b.cycle || "month"]}`;
    m.right = formatCZK(r.price); m.rightSub = "měsíčně";
    if (isStripe(r)) m.foot.push(<Strip key="p" tone="muted">{r.next_billing ? `Kartou · další platba ${dayShort(r.next_billing)}` : "Platí kartou přes Stripe"}</Strip>);
    else if (b.paid_until) m.foot.push(<Strip key="p" tone="ok" icon={CheckCircle2}>Zaplaceno do {dayShort(b.paid_until)}</Strip>);
    else m.foot.push(<Strip key="p" tone="warn" icon={AlertTriangle} onClick={() => ctx.onOpen(r.id, "sprava")}>Doplň „zaplaceno do“ — jinak se nehlídá</Strip>);
  } else if (col === "fakt") {
    m.sub = `${r.plan_label} · ${CYCLE_PERIOD[b.cycle || "month"]} od ${dayShort(b.next_start)}`;
    m.right = formatCZK(periodAmount(r)); m.rightSub = `za ${CYCLE_PERIOD[b.cycle || "month"]}`;
    m.foot.push(<Strip key="f" tone="warn" icon={FileText}>{ni
      ? `Faktura ${ni.number} · ${ni.status === "sent" ? "odeslaná" : ni.status === "paid" ? "zaplacená" : "neodeslaná"}`
      : ctx.faConfigured ? "Faktura se vystaví automaticky" : "Vystav fakturu — Fakturoid není napojený"}</Strip>);
  } else if (col === "ceka") {
    m.sub = isStripe(r) ? "Platba kartou neprošla"
      : b.phase === "first_due" ? `První platba · splatnost ${dayShort(b.first_due)}`
      : b.lock_postponed_until ? `Zámek odložen do ${dayShort(b.lock_postponed_until)}`
      : b.lock_on ? `Čeká na úhradu · zámek ${dayShort(b.lock_on)}` : "Čeká na úhradu";
    m.subTone = "pp-ink font-semibold";
    m.right = formatCZK(owedAmount(r)); m.rightSub = b.phase === "first_due" ? "k úhradě" : "dluží"; m.rightTone = "pp-ink";
    if (ni) m.foot.push(<Strip key="f" tone="muted" icon={FileText}>Faktura {ni.number} · {ni.status === "sent" ? "odeslaná" : "neodeslaná"}</Strip>);
  }
  if ((r.builds?.outdated || 0) >= 3) m.foot.push(<Strip key="b" tone="muted" icon={RefreshCw}>{r.builds.outdated} lidí na staré verzi aplikace</Strip>);
  if (over) m.foot.push(<Strip key="o" tone="warn" icon={AlertTriangle}>{r.active_projects}/{r.limit_active_projects ?? "∞"} zakázek · {r.user_count}/{r.limit_users ?? "∞"} lidí — nabídni vyšší tarif</Strip>);
  return m;
}

function FirmCard({ r, col, ctx, showSeg }) {
  const m = cardModel(r, col, ctx);
  const action = cardAction(r, col);
  const muted = segOf(r) === "archiv";
  return (
    <article onClick={() => ctx.onOpen(r.id)} data-testid={`sa-card-${r.id}`}
      className={`bg-card border rounded-[18px] p-3.5 cursor-pointer transition-[border-color,box-shadow] hover:shadow-md hover:border-foreground/20 motion-reduce:transition-none ${ctx.selectedId === r.id ? "border-brand ring-2 ring-brand/30" : "border-border"} ${muted ? "opacity-80" : ""}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <button type="button" onClick={(e) => { e.stopPropagation(); ctx.onOpen(r.id); }}
              className="text-left text-[15px] font-bold truncate min-w-0 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded">{r.name}</button>
            {showSeg && <span className="text-[11px] font-semibold text-muted-foreground whitespace-nowrap">{showSeg}</span>}
          </div>
          {m.pill && <div className="mt-1">{m.pill}</div>}
          {m.sub && <div className={`text-[13px] mt-0.5 leading-snug ${m.subTone || "text-muted-foreground"}`}>{m.sub}</div>}
        </div>
        {m.ring !== undefined ? <DaysRing n={m.ring} /> : m.right && (
          <div className="text-right shrink-0">
            <div className={`text-[19px] font-black tabular-nums whitespace-nowrap leading-tight ${m.rightTone}`}>{m.right}</div>
            {m.rightSub && <div className="text-[11px] text-muted-foreground whitespace-nowrap">{m.rightSub}</div>}
          </div>
        )}
      </div>
      {m.foot.length > 0 && <div className="mt-2.5 space-y-1.5">{m.foot}</div>}
      {action && (
        <Button size="sm" onClick={(e) => { e.stopPropagation(); ctx.run(action, r); }} disabled={ctx.busy}
          className="mt-3 h-10 px-4 rounded-full font-bold w-full sm:w-auto" data-testid={`sa-card-act-${r.id}`}>
          <action.icon size={15} aria-hidden="true" /> {action.label}
        </Button>
      )}
    </article>
  );
}

function ColumnHead({ col, title, count, extra, last }) {
  return (
    <div className="flex items-center gap-2 px-1 pb-2 min-w-0">
      <span className={`w-2 h-2 rounded-full shrink-0 ${DOT[col]}`} aria-hidden="true" />
      <h2 className="text-[17px] font-bold truncate">{title}</h2>
      <span className={`min-w-6 h-6 px-1.5 rounded-full text-xs font-bold grid place-items-center tabular-nums ${count && (col === "fakt" || col === "ceka") ? "bg-brand text-white" : "bg-card border border-border"}`}>{count}</span>
      <span className="ml-auto text-xs font-semibold text-muted-foreground tabular-nums whitespace-nowrap">{extra}</span>
      {!last && <ArrowRight size={14} className="hidden xl:block text-muted-foreground shrink-0" aria-hidden="true" />}
    </div>
  );
}

function EmptyCol({ col }) {
  const [t, s] = COL_EMPTY[col];
  const done = col === "fakt" || col === "ceka";
  return (
    <div className="rounded-[18px] border border-dashed border-border px-4 py-8 text-center">
      {done && <CheckCircle2 size={22} className="mx-auto text-success mb-1.5" aria-hidden="true" />}
      <div className="text-sm font-bold">{t}</div>
      {s && <div className="text-xs text-muted-foreground mt-0.5">{s}</div>}
    </div>
  );
}

function Skeleton() {
  return (
    <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className={`space-y-2 ${i ? "hidden md:block" : ""}`}>
          <div className="h-6 w-32 rounded-full bg-secondary animate-pulse motion-reduce:animate-none" />
          {[0, 1].map((j) => <div key={j} className="h-28 rounded-[18px] bg-card border border-border animate-pulse motion-reduce:animate-none" />)}
        </div>
      ))}
    </div>
  );
}

export default function SuperadminPipeline({ kpi, rows, loading, fa, onFakturoid, onOpen, selectedId, run, busy }) {
  const [ocrOpen, setOcrOpen] = useState(false);
  const [q, setQ] = useState("");
  const [seg, setSeg] = useState("aktivni");
  const [mcol, setMcol] = useState(null);
  const nq = norm(q.trim());
  const ctx = { onOpen, run, busy, selectedId, faConfigured: !!fa?.configured };

  const view = useMemo(() => {
    const segs = { aktivni: [], pauza: [], archiv: [], test: [] };
    rows.forEach((r) => segs[segOf(r)].push(r));
    Object.keys(segs).forEach((k) => segs[k].sort(sortFor(k)));
    const cols = { demo: [], plati: [], fakt: [], ceka: [] };
    segs.aktivni.forEach((r) => cols[colOf(r)].push(r));
    Object.keys(cols).forEach((k) => cols[k].sort(sortFor(k)));
    return { segs, cols };
  }, [rows]);

  const hits = useMemo(() => (nq ? rows.filter((r) => norm(r.name).includes(nq) || String(r.ico || "").includes(q.trim())) : []), [rows, nq, q]);
  // Čísla souhrnu z jedné množiny: platící = placený tarif (i čekající / zamčení za neplacení), aktivní = demo + platící.
  const regs = rows.filter((r) => !r.is_test).length;
  const payingRows = rows.filter(isPaying);
  const paying = payingRows.length;
  const active = view.cols.demo.length + paying;
  const mrr = payingRows.reduce((a, r) => a + (r.price || 0), 0);
  const conv = regs ? Math.round((paying / regs) * 100) : 0;
  const lockedRows = view.segs.pauza.filter((r) => isPaying(r));
  const dateLine = new Intl.DateTimeFormat("cs-CZ", { weekday: "long", day: "numeric", month: "numeric", timeZone: TZ }).format(new Date());
  const defaultCol = ["ceka", "fakt", "demo", "plati"].find((k) => view.cols[k].length) || "demo";
  const curCol = mcol || defaultCol;
  const colExtra = {
    demo: "podle konce dema",
    plati: `${formatCZK(view.cols.plati.reduce((a, r) => a + (r.price || 0), 0))}/měs`,
    fakt: formatCZK(view.cols.fakt.reduce((a, r) => a + periodAmount(r), 0)),
    ceka: formatCZK(view.cols.ceka.reduce((a, r) => a + owedAmount(r), 0)),
  };
  const segName = Object.fromEntries(SEGMENTS);

  return (
    <div className="space-y-4 md:space-y-5" data-testid="superadmin-page">
      {/* Hlavička: na telefonu hledání pod nadpisem, na počítači vpravo vedle Fakturoidu */}
      <header className="flex flex-wrap items-center gap-x-3 gap-y-3">
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-[28px] md:text-[34px] font-black tracking-tight leading-tight">Superadmin</h1>
          <p className="text-sm text-muted-foreground first-letter:uppercase">{dateLine} · {regs} {pl(regs, "firma", "firmy", "firem")}</p>
        </div>
        <label className="order-3 lg:order-2 basis-full lg:basis-[360px] min-w-0 h-11 rounded-full bg-card border border-border flex items-center gap-2 px-3.5 focus-within:ring-2 focus-within:ring-brand">
          <Search size={17} className="text-muted-foreground shrink-0" aria-hidden="true" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat firmu nebo IČO" aria-label="Hledat firmu nebo IČO" data-testid="admin-search"
            className="flex-1 min-w-0 bg-transparent outline-none text-base md:text-sm text-foreground placeholder:text-muted-foreground" />
          {q && <button type="button" onClick={() => setQ("")} aria-label="Smazat hledání" className="w-8 h-8 grid place-items-center rounded-full text-muted-foreground hover:bg-secondary"><X size={15} /></button>}
        </label>
        <button type="button" onClick={onFakturoid} data-testid="open-fakturoid"
          className="order-2 lg:order-3 h-11 px-4 rounded-full bg-card border border-border inline-flex items-center gap-2 text-sm font-bold shrink-0 hover:bg-secondary">
          <FileText size={16} aria-hidden="true" /> Fakturoid
          <span className={`w-2 h-2 rounded-full ${fa?.configured ? "bg-success" : "bg-muted-foreground/40"}`} aria-label={fa?.configured ? "napojeno" : "nenapojeno"} />
        </button>
      </header>

      {/* Souhrn: MRR · cesta zákazníků · konverze · OCR */}
      <section className="bg-card border border-border rounded-[24px] p-4 md:p-5" data-testid="sa-summary">
        <div className="grid gap-4 lg:grid-cols-[auto_minmax(0,1fr)] xl:grid-cols-[auto_minmax(0,1fr)_auto_auto] lg:items-center">
          <div className="min-w-0 lg:pr-6 lg:border-r border-border">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1 text-sm font-semibold text-muted-foreground">Měsíční příjem (MRR)
                <InfoTip label="Jak se počítá MRR">Součet měsíčních cen tarifů firem s placeným tarifem — i těch, které čekají na úhradu nebo jsou za neplacení zamčené (bez DPH, neplátce). Testovací účty se nepočítají.</InfoTip></div>
              <span className="xl:hidden px-2.5 py-1 rounded-full bg-success/15 text-success text-xs font-bold tabular-nums">konverze {conv} %</span>
            </div>
            <div className="mt-1 flex items-baseline gap-1.5 whitespace-nowrap" title={formatCZK(mrr)}>
              <span className="font-heading text-[34px] md:text-[42px] font-black tracking-tight leading-none tabular-nums" data-testid="sa-mrr">{rows.length || !loading ? formatCZK(mrr) : "—"}</span>
              <span className="text-base font-bold text-muted-foreground">/ měs</span>
            </div>
          </div>
          <div className="min-w-0 xl:px-6 xl:border-r border-border">
            <div className="hidden md:block text-sm font-semibold text-muted-foreground mb-1">Cesta zákazníků</div>
            <div className="flex items-baseline gap-2 flex-wrap" data-testid="sa-funnel">
              <span className="text-[22px] md:text-[26px] font-black tabular-nums">{regs}</span><span className="text-sm text-muted-foreground">{pl(regs, "registrace", "registrace", "registrací")}</span>
              <ChevronRight size={16} className="text-muted-foreground self-center" aria-hidden="true" />
              <span className="text-[22px] md:text-[26px] font-black tabular-nums">{active}</span><span className="text-sm text-muted-foreground">{pl(active, "aktivní", "aktivní", "aktivních")}</span>
              <ChevronRight size={16} className="text-muted-foreground self-center" aria-hidden="true" />
              <span className="text-[22px] md:text-[26px] font-black tabular-nums text-success">{paying}</span><span className="text-sm text-muted-foreground">platí</span>
            </div>
            <div className="mt-2 h-2 rounded-full bg-secondary overflow-hidden flex" aria-hidden="true">
              <span className="h-full bg-success" style={{ width: `${regs ? (paying / regs) * 100 : 0}%` }} />
              <span className="h-full bg-muted-foreground/40" style={{ width: `${regs ? ((active - paying) / regs) * 100 : 0}%` }} />
            </div>
            <div className="xl:hidden mt-2 text-xs text-muted-foreground flex flex-wrap gap-x-3">
              <span>{paying} ze {regs} firem platí</span><button type="button" onClick={() => setOcrOpen(true)} className="underline decoration-dotted underline-offset-2" data-testid="ocr-stats-open-m">OCR tento měsíc <b className="text-foreground tabular-nums">{kpi ? formatCZK(kpi.ocr_cost_month) : "—"}</b></button>
            </div>
          </div>
          <div className="hidden xl:block px-6 border-r border-border">
            <div className="text-sm font-semibold text-muted-foreground">Konverze</div>
            <div className="text-[26px] font-black tabular-nums text-success leading-tight">{conv} %</div>
            <div className="text-xs text-muted-foreground">{paying} ze {regs} firem</div>
          </div>
          <button type="button" onClick={() => setOcrOpen(true)} className="hidden xl:block pl-6 text-left rounded-[12px] hover:bg-secondary/60 -my-1 py-1 pr-2" data-testid="ocr-stats-open">
            <div className="text-sm font-semibold text-muted-foreground">OCR tento měsíc</div>
            <div className="text-[26px] font-black tabular-nums leading-tight whitespace-nowrap">{kpi ? formatCZK(kpi.ocr_cost_month) : "—"}</div>
            <div className="text-xs text-muted-foreground underline decoration-dotted underline-offset-2">čtení dokladů · měření</div>
          </button>
        </div>
      </section>
      {ocrOpen && <OcrStatsDialog onClose={() => setOcrOpen(false)} />}

      {/* Filtr stavů — každá firma je právě v jednom */}
      {!nq && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div role="tablist" aria-label="Stav firem" className="w-full sm:w-auto grid grid-cols-4 sm:flex gap-1 p-1 rounded-[18px] sm:rounded-full bg-card border border-border">
            {SEGMENTS.map(([k, l]) => (
              <button key={k} type="button" role="tab" aria-selected={seg === k} onClick={() => setSeg(k)} data-testid={`sa-seg-${k}`}
                className={`min-h-[52px] sm:min-h-0 sm:h-10 px-1 sm:px-3.5 rounded-[14px] sm:rounded-full font-bold whitespace-nowrap transition-colors sm:inline-flex sm:items-center sm:gap-1 ${seg === k ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
                <span className="block sm:inline text-[18px] sm:text-xs sm:order-2 font-black sm:font-bold tabular-nums leading-tight sm:opacity-70">{view.segs[k].length}</span>
                <span className="block sm:inline text-[11px] sm:text-sm truncate">{k === "pauza" ? <><span className="sm:hidden">Pauza</span><span className="hidden sm:inline">{l}</span></> : k === "test" ? <><span className="sm:hidden">Test</span><span className="hidden sm:inline">{l}</span></> : l}</span>
              </button>
            ))}
          </div>
          {seg === "aktivni"
            ? <span className="hidden sm:inline-flex"><InfoTip label="Co znamenají stavy">
                <b>Aktivní</b> — platí nebo běží demo. <b>Pozastavené</b> — teď nemůžou pracovat (ručně pozastavené, zamčené za neplacení, vypršelé demo), vrátíš je jedním klepnutím. <b>Archiv</b> — ukončená spolupráce. <b>Testovací</b> — tvoje účty, nepočítají se do čísel.
              </InfoTip></span>
            : <p className="text-sm text-muted-foreground">{SEG_NOTE[seg]}</p>}
        </div>
      )}

      {loading && !rows.length ? <Skeleton /> : nq ? (
        <section data-testid="sa-search-results">
          <p className="text-sm text-muted-foreground px-1 mb-2" role="status">{hits.length ? `Výsledky pro „${q.trim()}“ · ${hits.length}` : `Nic nenalezeno pro „${q.trim()}“.`}</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {hits.map((r) => <FirmCard key={r.id} r={r} col={colOf(r)} ctx={ctx} showSeg={segOf(r) === "aktivni" ? Object.fromEntries(COLUMNS)[colOf(r)] : segName[segOf(r)]} />)}
          </div>
        </section>
      ) : seg === "aktivni" ? (
        <>
          {/* Telefon: přepínač sloupců místo čtyř vedle sebe */}
          <div className="md:hidden grid grid-cols-4 gap-1 p-1 rounded-[18px] bg-card border border-border" role="tablist" aria-label="Sloupec">
            {COLUMNS.map(([k, l]) => (
              <button key={k} type="button" role="tab" aria-selected={curCol === k} onClick={() => setMcol(k)} data-testid={`sa-mcol-${k}`}
                className={`min-h-[56px] rounded-[14px] px-1 transition-colors ${curCol === k ? "bg-foreground text-background" : "text-muted-foreground"}`}>
                <span className="block text-[20px] font-black tabular-nums leading-tight">{view.cols[k].length}</span>
                <span className="block text-[11px] font-bold truncate">{k === "ceka" ? "Čeká" : k === "demo" ? "Demo" : l}</span>
              </button>
            ))}
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3 items-start" data-testid="sa-board">
            {COLUMNS.map(([k, l], i) => (
              <section key={k} className={`${curCol === k ? "" : "hidden"} md:block min-w-0`} data-testid={`sa-col-${k}`}>
                <ColumnHead col={k} title={l} count={view.cols[k].length} extra={colExtra[k]} last={i === COLUMNS.length - 1} />
                <div className="md:bg-secondary/50 md:rounded-[22px] md:p-2 space-y-2 md:min-h-[320px]">
                  {view.cols[k].map((r) => <FirmCard key={r.id} r={r} col={k} ctx={ctx} />)}
                  {k === "ceka" && lockedRows.length > 0 && (
                    <button type="button" onClick={() => setSeg("pauza")} data-testid="sa-locked-link"
                      className="w-full text-left rounded-[18px] border border-rose-500/40 bg-rose-500/5 px-3.5 py-3 text-sm hover:bg-rose-500/10">
                      <span className="flex items-center gap-1.5 font-bold text-rose-600 dark:text-rose-400"><Lock size={14} aria-hidden="true" /> {lockedRows.length} {pl(lockedRows.length, "zamčená", "zamčené", "zamčených")} za neplacení · {formatCZK(lockedRows.reduce((a, r) => a + owedAmount(r), 0))}</span>
                      <span className="block text-xs text-muted-foreground mt-0.5">Jsou v Pozastavených — zobrazit ›</span>
                    </button>
                  )}
                  {!view.cols[k].length && !(k === "ceka" && lockedRows.length) && <EmptyCol col={k} />}
                </div>
              </section>
            ))}
          </div>
        </>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4" data-testid={`sa-grid-${seg}`}>
          {view.segs[seg].length ? view.segs[seg].map((r) => <FirmCard key={r.id} r={r} col={colOf(r)} ctx={ctx} />)
            : <p className="text-sm text-muted-foreground px-1">Nic tu není.</p>}
        </div>
      )}
    </div>
  );
}
